import React, { useState, useEffect } from "react";

import QuizGoogleSheet from "./QuizGoogleSheet";
import SyllabusOverview from "./SyllabusOverview";

import "./quizlayout.css";

export default function QuizLoader() {
  const subjects = [
    {
      id: "crp",
      title: "The Code of Criminal Procedure, 1898",
      subtitle: "Criminal procedure, powers of courts, investigation & trial",
      color: "#2563eb",
      icon: "⚖️",
    },
    {
      id: "cpc",
      title: "The Code of Civil Procedure, 1908",
      subtitle: "Civil suits, decrees, orders and execution",
      color: "#10b981",
      icon: "📘",
    },
    {
      id: "tax",
      title: "Income Tax Act, 2023",
      subtitle: "Assessment, TDS, penalties & appeals",
      color: "#f59e0b",
      icon: "💰",
    },
    {
      id: "professionalethics",
      title: "Professional Ethics & Bar Council Order",
      subtitle: "Advocate conduct, discipline & standards",
      color: "#ec4899",
      icon: "🧑‍⚖️",
    },
    {
      id: "penalcode",
      title: "Penal Code, 1860",
      subtitle: "Offences, punishment & general exceptions",
      color: "#ef4444",
      icon: "🚨",
    },
    {
      id: "theevidenceact",
      title: "The Evidence Act, 1872",
      subtitle: "Relevancy, admissibility & burden of proof",
      color: "#8b5cf6",
      icon: "📑",
    },
    {
      id: "thelimitionact",
      title: "The Limitation Act",
      subtitle: "Limitation periods of suits & appeals",
      color: "#0ea5e9",
      icon: "⏳",
    },
    {
      id: "specificreliefact",
      title: "The Specific Relief Act, 1877",
      subtitle: "Specific performance & injunctions",
      color: "#14b8a6",
      icon: "📝",
    },
    {
      id: "slybus",
      title: "Complete Syllabus Overview",
      subtitle: "All subjects complete syllabus list",
      color: "#6b7280",
      icon: "📚",
    },
  ];

  const [active, setActive] = useState(subjects[0].id);
  const [darkMode, setDarkMode] = useState(false);

  // 60-minute timer
  const EXAM_DURATION = 60 * 60; // 60 minutes in seconds
  const [timeLeft, setTimeLeft] = useState(EXAM_DURATION);
  const [autoSubmitted, setAutoSubmitted] = useState(false);

  // Scores
  const [score, setScore] = useState(0);
  const [attempted, setAttempted] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);

  const info = subjects.find((s) => s.id === active) || subjects[0];

  // Reset exam when subject changes (except syllabus)
  useEffect(() => {
    if (active === "slybus") return;
    setTimeLeft(EXAM_DURATION);
    setScore(0);
    setAttempted(0);
    setTotalQuestions(0);
    setAutoSubmitted(false);
  }, [active]);

  // Timer + auto-submit event
  useEffect(() => {
    if (active === "slybus") return;

    if (timeLeft <= 0) {
      if (!autoSubmitted) {
        window.dispatchEvent(new Event("autoSubmitExam"));
        setAutoSubmitted(true);
      }
      return;
    }

    const t = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(t);
  }, [timeLeft, active, autoSubmitted]);

  // Warning sound at last 5 minutes
  useEffect(() => {
    if (active === "slybus") return;
    if (timeLeft === 300) {
      const audio = document.getElementById("warning-sound");
      if (audio) {
        audio.play().catch(() => {});
      }
    }
  }, [timeLeft, active]);

  const formatTime = (sec) => {
    const m = String(Math.floor(sec / 60)).padStart(2, "0");
    const s = String(sec % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  const accuracy =
    totalQuestions > 0 ? Math.round((score / totalQuestions) * 100) : 0;

  // Exit fullscreen when selecting syllabus
  useEffect(() => {
    if (active === "slybus" && document.fullscreenElement) {
      document.exitFullscreen();
    }
  }, [active]);

  return (
    <>
      {/* Warning sound element (place warning.mp3 in public folder) */}
      <audio id="warning-sound" src="/warning.mp3" preload="auto" />

      <div className={`layout-root ${darkMode ? "dark" : ""}`}>
        <div className="layout-container">
          {/* Sidebar */}
          <aside className="sidebar">
            {subjects.map((s) => (
              <button
                key={s.id}
                className={`sidebar-btn ${active === s.id ? "active" : ""}`}
                onClick={() => setActive(s.id)}
                style={{
                  borderLeftColor: active === s.id ? s.color : "transparent",
                }}
              >
                <span className="sidebar-icon">{s.icon}</span>
                <span className="sidebar-text">{s.title}</span>
              </button>
            ))}
          </aside>

          {/* Mobile Dropdown */}
          <div className="mobile-dropdown">
            <select value={active} onChange={(e) => setActive(e.target.value)}>
              {subjects.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.icon} {s.title}
                </option>
              ))}
            </select>
          </div>

          {/* Tablet slider */}
          <div className="tab-slider">
            {subjects.map((s) => (
              <button
                key={s.id}
                className={`slider-btn ${active === s.id ? "active" : ""}`}
                onClick={() => setActive(s.id)}
                style={{
                  background: active === s.id ? s.color : "#ddd",
                  color: active === s.id ? "#fff" : "#000",
                }}
              >
                {s.icon} {s.title}
              </button>
            ))}
          </div>

          {/* Main Content */}
          <main className="content-area">
            {/* Hide header on syllabus tab */}
            {active !== "slybus" && (
              <div className="exam-header">
                <div className="exam-title-block">
                  <h1 style={{ color: info.color }}>
                    {info.icon} {info.title}
                  </h1>
                  <p className="subject-subtitle">{info.subtitle}</p>
                </div>

                {/* Sticky Timer & Score */}
                <div
                  className="exam-meta-card"
                  style={{
                    position: "fixed",
                    top: "100px",
                    right: "20px",
                    width: "210px",
                    background: "#fff",
                    border: "1px solid #ddd",
                    padding: "12px",
                    borderRadius: "8px",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                    zIndex: 1000,
                  }}
                >
                  <div className="timer-row">
                    <span className="meta-label">Time Left</span>
                    <span className="meta-value">
                      {timeLeft > 0 ? formatTime(timeLeft) : "00:00"}
                    </span>
                  </div>

                  <div className="score-row">
                    <span className="meta-label">Score</span>
                    <span className="meta-value">{score.toFixed(2)}</span>
                  </div>

                  <div className="score-row">
                    <span className="meta-label">Attempted</span>
                    <span className="meta-value">{attempted}</span>
                  </div>

                  <div className="score-row">
                    <span className="meta-label">Accuracy</span>
                    <span className="meta-value">
                      {totalQuestions ? `${accuracy}%` : "—"}
                    </span>
                  </div>

                  {/* Restart Button */}
                  <button
                    onClick={() => {
                      setTimeLeft(EXAM_DURATION);
                      setScore(0);
                      setAttempted(0);
                      setTotalQuestions(0);
                      setAutoSubmitted(false);
                    }}
                    style={{
                      marginTop: "10px",
                      width: "100%",
                      padding: "8px",
                      borderRadius: "6px",
                      background: info.color,
                      color: "white",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    Restart Exam
                  </button>

                  {/* Full Screen Button */}
                  <button
                    onClick={() => {
                      if (!document.fullscreenElement) {
                        document.documentElement.requestFullscreen();
                      } else {
                        document.exitFullscreen();
                      }
                    }}
                    style={{
                      marginTop: "10px",
                      width: "100%",
                      padding: "8px",
                      borderRadius: "6px",
                      background: "#333",
                      color: "white",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    Full Screen
                  </button>
                </div>
              </div>
            )}

            {/* Show either syllabus or quiz */}
            {active === "slybus" ? (
              <SyllabusOverview />
            ) : (
              <QuizGoogleSheet
                sheetName={active}
                timeLeft={timeLeft}
                totalDuration={EXAM_DURATION}
                onMetaChange={({ total }) => setTotalQuestions(total)}
                onProgressChange={({ attemptedCount }) =>
                  setAttempted(attemptedCount)
                }
                onScoreChange={(newScore) => setScore(newScore)}
              />
            )}
          </main>
        </div>
      </div>
    </>
  );
}
